import { useCallback, useEffect, useMemo, useState } from 'react';
import { DSIcon, DSTextInput } from '@eiv2/design-system';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { RequestState } from '../components/RequestState';
import { CollapsibleFiltersCard } from '../components/CollapsibleFiltersCard';
import { DateRangePicker } from '../components/DateRangePicker';
import { IconTooltipButton } from '../components/IconTooltipButton';
import { DataGridFrame, visibleDataGridRows } from '../components/DataGridFrame';
import { adminApi } from '../api/client';
import { usePaginatedList } from '../hooks/usePaginatedList';
import { LoadMoreButton } from '../components/LoadMoreButton';
import { formatDateTime } from '../services/format';
import { getEventNameLabel } from '../services/labels';
import { getDefaultWindow, type DateRange } from '../services/dateWindow';
import { dataGridLocaleText } from '../theme/dataGridLocale';
import type { AdminEvent } from '../types/api';

export function EventsPage({
  api = adminApi,
  organizer = false,
  initialWindow
}: {
  api?: Pick<typeof adminApi, 'listEvents'>;
  organizer?: boolean;
  initialWindow?: DateRange;
} = {}) {
  const [window, setWindow] = useState<DateRange>(() => initialWindow ?? getDefaultWindow());
  const [eventNameDraft, setEventNameDraft] = useState('');
  const [eventName, setEventName] = useState('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10
  });

  useEffect(() => {
    const id = setTimeout(() => setEventName(eventNameDraft.trim()), 300);
    return () => clearTimeout(id);
  }, [eventNameDraft]);

  const fetcher = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      api.listEvents(
        {
          from: window.from,
          to: window.to,
          eventName: eventName || undefined,
          limit: 50,
          cursor: cursor ?? undefined
        },
        signal
      ),
    [api, window.from, window.to, eventName]
  );

  const list = usePaginatedList<AdminEvent>(fetcher, [api, window.from, window.to, eventName]);

  const rows = useMemo(() => list.items.map((e) => ({ ...e, id: e.eventId })), [list.items]);

  const columns: GridColDef[] = [
    { field: 'organizationId', headerName: 'Organização', width: 140 },
    {
      field: 'eventName',
      headerName: 'Evento',
      flex: 1,
      minWidth: 180,
      valueFormatter: (value: string) => getEventNameLabel(value)
    },
    { field: 'checkoutId', headerName: 'Carrinho', flex: 1, minWidth: 160 },
    { field: 'sessionId', headerName: 'Sessão', flex: 1, minWidth: 160 },
    { field: 'anonymousId', headerName: 'Anônimo', flex: 1, minWidth: 140 },
    { field: 'page', headerName: 'Página', flex: 1, minWidth: 160 },
    {
      field: 'receivedAt',
      headerName: 'Quando',
      flex: 1.2,
      minWidth: 220,
      valueFormatter: (value: string) => formatDateTime(value)
    }
  ];

  return (
    <div>
      <PageHeader
        title={organizer ? 'Atividades' : 'Eventos'}
        subtitle="Eventos recentes capturados pelo rastreamento."
      />

      <CollapsibleFiltersCard
        className="mb-6"
        storageKey="events"
        title="Filtrar por período e evento"
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          <DateRangePicker className="lg:max-w-[520px]" value={window} onChange={setWindow} />
          <DSTextInput
            className="min-w-0 w-full flex-1"
            classNames={{
              field: 'min-h-14 bg-light-gray'
            }}
            label="Nome do evento"
            onChange={(e) => setEventNameDraft(e.target.value)}
            placeholder="checkout_opened…"
            value={eventNameDraft}
          />
        </div>
      </CollapsibleFiltersCard>

      {list.error ? (
        <RequestState
          title="Não foi possível carregar os eventos"
          error={list.error}
          onRetry={list.reload}
        />
      ) : list.isLoading && rows.length === 0 ? (
        <RequestState title="Carregando eventos" message="Buscando os eventos mais recentes." />
      ) : (
        <SectionCard
          action={
            <IconTooltipButton disabled={list.isLoading} label="Atualizar" onClick={list.reload}>
              <DSIcon name="Refresh" size={20} />
            </IconTooltipButton>
          }
          title="Fluxo de eventos"
          subtitle="Eventos mais recentes capturados no período filtrado"
        >
          <DataGridFrame
            visibleRowCount={visibleDataGridRows(
              rows.length,
              paginationModel.page,
              paginationModel.pageSize
            )}
          >
            <DataGrid
              rows={rows}
              columns={
                organizer
                  ? columns.filter(
                      (column) =>
                        !['organizationId', 'sessionId', 'anonymousId'].includes(column.field)
                    )
                  : columns
              }
              disableRowSelectionOnClick
              localeText={{
                ...dataGridLocaleText,
                noRowsLabel: 'Nenhum evento encontrado com esses filtros.'
              }}
              pageSizeOptions={[10, 25, 50]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
            />
          </DataGridFrame>
          <LoadMoreButton
            nextCursor={list.nextCursor}
            onLoadMore={list.loadMore}
            isLoading={list.isLoading || list.isLoadingMore}
          />
        </SectionCard>
      )}
    </div>
  );
}
